import { ApplicationStatusType, PropertyStatusType } from '../../../constants/src';
import { ApiResponse, BfiApi, IApiResponse } from './bfiApi';
import { ISearchCriteria, IBfiNumberCheck } from './common';
import { IBorrowersListItem } from './loanApi';
import { LoanType } from '../../../constants/src';
import { IAddress } from 'libs/store/src/cross';
import { AxiosRequestConfig } from 'axios';

export interface IRecentApplication {
    name: string;
    entity: string;
    loanTypeId: number;
    createdAt: Date;
}

export interface IRecentApplicationResponse extends Omit<IApiResponse, 'result'> {
    result: [IRecentApplication];
}

export interface IPortfolioBase {
    id: number;
    disabled: boolean;
    propertyId?: number;
    bfiNumber: string;
    appStatusId: ApplicationStatusType;
    propertyStatusId: PropertyStatusType;
    address: IAddress;
}

export interface IGridPortfolioAppraisal extends IPortfolioBase {
    orderedDate?: Date;
    expectedDate?: Date;
    receivedDate?: Date;
    reviewedDate?: Date;
    cost?: number;
    notes: string;
    appriaserCompany: string;
    appraiserReviewer: string;
    daysOverdue?: number;
    datsSinceRecieved?: number;
}

export interface IGridAppBase {
    id: number;
    appStatusId: ApplicationStatusType;
    propertyStatusId: PropertyStatusType;
    isPortfolio: boolean;
    baseBfiNumber: string;
    bfiNumberSort: string;
    borrowerName: string;
    borrowerEntity: string;
    borrower: IBorrower;
    createdDate: Date;
    daysSinceApplication: number;
    loanTypeId: LoanType;
    lastEditedByName: string;
    responsibleName: string;
    hightlight: boolean;
    isPaid: boolean;
}

export interface IGridAppProperty extends IGridAppBase {
    propertyId: number;
    bfiNumber: number;
    address: IAddress;
    borrower: IBorrower;
}

export interface IGridAppDeclined extends IGridAppBase {
    bfiNumber: number;
    address: IAddress;
    noticeSentDate?: Date;
    noticeSentBy: string;
}

export interface IGridAppAppraisal extends IGridAppBase {
    propertyId?: number;
    bfiNumber: number;
    address: IAddress;
    portfolio: [IGridPortfolioAppraisal];
    orderedDate?: Date;
    expectedDate?: Date;
    receivedDate?: Date;
    reviewedDate?: Date;
    cost?: number;
    notes: string;
    appriaserCompany: string;
    appraiserReviewer: string;
    daysOverdue?: number;
    datsSinceRecieved?: number;
    canConvertLoan: boolean;
    propertyCity: string;
    propertyState: string;
}

export interface IGridAppPropertyWithFilesStatus extends IGridAppProperty {
    fileStatusBudget: number;
    fileStatusPlans: number;
    fileStatusSpecSheet: number;
    fileStatusSurvey: number;
    fileStatusProofOfOccupancy: number;
}

export interface IGridApplPropertyApproved extends IGridAppProperty {
    MasterNoteBfiNumber: string;
    ApprovalDate: Date;
    LoanStatusId: number;
}

export interface IGridApplPropertyAppraisal extends IGridAppProperty {
    orderedDate: Date;
    expectedDate?: Date;
    receivedDate?: Date;
    reviewedDate?: Date;
    cost: number;
    notes: string;
    appraiserCompany: string;
    appraiserReviewer: string;
    daysOverdue: number;
}

export interface IGridAppIncomplete {
    id: number;
    borrowerName: string;
    borrowerEntity: string;
    borrowerEmail: string;
    loanTypeId: LoanType;
    createdAt: Date;
    updatedAt: Date;
    percentComplete: number;
    pointOfContact: string;
}

export interface IBorrower {
    FirstName: string;
    MiddleName: string;
    LastName: string;
    email: string;
    phone: string;
    secondaryEmail: string;
    Address: IAddress;
}

export interface IPropertyReviewFile {
    id?: number;
    fileType: number;
    typeName: string;
    url: string;
    statusId: number;
    statusChangedByMsg: string;
}

export interface IPropertyReview {
    id: number;
    bfiNumberSuffix: string;
    statusId: PropertyStatusType;
    statusChangedByMsg;
    string;
    address: IAddress;
    description: string;
    files: IPropertyReviewFile[];
}

export interface ISubmittedAppReview {
    id: number;
    applicationId: number;
    responsibleId?: number;
    loanDescription: string;
    contactName: string;
    entityName: string;
    bfiNumber: string;
    loanNumber?: number;
    Files: IPropertyReviewFile[];
    propertyFiles: IPropertyReviewFile[];
    createdDate: Date;
    properties: IPropertyReview[];
}

export interface IIncompleteAppReview {
    id: number;
    responsibleId?: number;
    loanDescription: string;
    contactName: string;
    entityName: string;
    createdDate: Date;
}

export interface IApplicationSummary {
    id: number;
    applicationId: number;
    responsibleId?: number;
    address: IAddress;
    description: string;
    loanDescription: string;
    contactName: string;
    entityName: number;
    bfiNumber: string;
    loanNumber?: number;
    createdDate: Date;
}

export interface IApplicationFileSummary {
    id: number;
    applicationId: number;
    responsibleId?: number;
    loanDescription: string;
    contactName: string;
    entityName: string;
    bfiNumber: string;
    loanNumber?: number;
    files: IPropertyReviewFile[];
    propertyFiles: IPropertyReviewFile[];
    createdDate: Date;
    properties: IPropertyReview[];
}

export interface IPropertyReviewUpdate {
    id: number;
    bfiNumber: string;
    loanNumber?: number;
    responsibleId?: number;
    isPaid: boolean;
    statusId: PropertyStatusType;
    applicationFiles: IPropertyReviewFile[];
    propertyFiles: IPropertyReviewFile[];
}

export interface IGridIncomplete {
    id: number;
    borrowerName: string;
    borrowerEntity: string;
    borrowerEmail: string;
    loanTypeId: LoanType;
    createdAt: Date;
    updatedAt: Date;
    percentComplete: number;
    pointOfContact: string;
}

export interface IGridIncompleteResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IGridIncomplete];
    };
}

export interface IGridApplPropertyResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IGridAppProperty];
    };
}

export interface IGridApplPropertyApprovedResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IGridApplPropertyApproved];
    };
}

export interface IApplicationGridRow {
    id: number;
    name: string;
    entity: string;
    bfiNumber: number;
    daysSinceApplication: number;
    street1: string;
    street2: string;
    city: string;
    state: number;
    loanTypeId: LoanType;
    IsPaid: boolean;
    budgetStatusId: number;
    specSheetStatusId: number;
    plansStatusId: number;
    surveyStatusId: number;
    responsible: string;
    highlight: boolean;
}

export interface IDeclinedGridRow {
    id: number;
    name: string;
    entity: string;
    bfiNumber: number;
    street1: string;
    street2: string;
    city: string;
    state: number;
    loanTypeId: LoanType;
    CreatedDate: Date;
}

export interface IAppraisalOrderedGridRow {
    id: number;
    name: string;
    entity: string;
    ordered: Date;
    expected: Date;
    location: string;
    loanTypeId: LoanType;
    appraiser: string;
    cost: number;
    notes: string;
    daysOverdue: number;
    highlight: boolean;
}

export interface IAppraisalReceivedGridRow {
    id: number;
    name: string;
    entity: string;
    receivedDate: Date;
    location: string;
    loanTypeId: LoanType;
    appraiser: string;
    reviewer: string;
    daysSinceReceived: number;
    highlight: boolean;
}

export interface IAppraisalReviewedGridRow {
    id: number;
    name: string;
    entity: string;
    location: string;
    loanTypeId: LoanType;
    requestedDate: Date;
    orderedDate: Date;
    expectedDate: Date;
    receivedDate: Date;
    cost: number;
    appraiser: string;
    reviewer: string;
    highlight: boolean;
}

export interface IPropertyModalBase {
    id: number;
    bfiNumberSuffix: string;
    statusId: PropertyStatusType;
    address: IAddress;
    description: string;
    disabled: boolean;
}

export interface IAppraisalFile {
    id: number;
    fileName: string;
    isFinal: boolean;
    url: string;
}

export interface IAppraiseProperty extends IPropertyModalBase {
    appraiserId: number;
    appraiserName: string;
    orderDate: Date;
    expectedDate: Date;
    receivedDate?: Date;
    notes: string;
    cost: number;
    reviewedDate?: Date;
    reviewerId?: number;
    file: IAppraisalFile;
}

export interface IAppraisePropertyNotes {
    id: number;
    notes: string;
}

export interface IApplicationModalBase {
    id: number;
    isPortfolio: boolean;
    LoanDescription: string;
    contactName: string;
    entityName: string;
    bfiNumber: string;
    loanNumber?: number;
}

export interface IAppraiseApplication extends IApplicationModalBase {
    properties: [IAppraiseProperty];
}

export interface IAppraiseApplicationNotes {
    id: number;
    properties: [IAppraisePropertyNotes];
}

export interface IHighlights {
    pending: boolean;
    appraise: boolean;
    decline: boolean;
}

export interface ICheckLoanAndBfiNumber {
    loanNumber: boolean;
    bfiNumber: boolean;
}

export interface IRollbackProperty extends IPropertyModalBase {
    pending: boolean;
    appraise_Reviewed: boolean;
    appraise_Received: boolean;
    appraise_Ordered: boolean;
    appraise_BfiToOrder: boolean;
}

export interface IRollbackApplication extends IApplicationModalBase {
    properties: [IRollbackProperty];
}

export interface IRollbackPropertyRequest {
    id: number;
    statusId: PropertyStatusType;
}

export interface IRollbackApplicationRequest {
    id: number;
    properties: [IRollbackPropertyRequest];
}

export interface IMasterNoteItem {
    id: number;
    bfiNumber: string;
    commitment: number;
}

export interface IGetLoanToConvert {
    id: number;
    bfiNumber: string;
    loanNumber?: number;
    borrowerEntity: string;
    borrowerName: string;
    masterNotes: [IMasterNoteItem];
    borrowers: [IBorrowersListItem];
}

export interface IConvertToLoan {
    applicationId: number;
    approvalDate: Date;
    bfiNumber: string;
    loanNumber: number;
    masterNoteId?: number;
    borrowerEntityId?: number;
    brokerAgreementId?: number;
    propertyIds: [number];
}

export interface IChangeLoanType {
    id: number;
    loanTypeId: LoanType;
    masterNoteId?: number | null;
}

export interface IDeclineRejectProperty {
    id: number;
    newStatusId: PropertyStatusType;
}

export interface IDeclineNotifyProperty {
    id: number;
    dateSent: Date;
}

export interface IDeclineRejectApplication {
    id: number;
    properties: [IDeclineRejectProperty];
}

export interface IDeclineNotifyApplication {
    id: number;
    properties: [IDeclineNotifyProperty];
}

export interface IIncompleteAppReviewUpdate {
    id: number;
    pocId: string;
}

export interface ISubmittedAppReviewResponse extends Omit<IApiResponse, 'result'> {
    result: [ISubmittedAppReview];
}

export interface IIncompleteAppReviewResponse extends Omit<IApiResponse, 'result'> {
    result: [IIncompleteAppReview];
}

export interface IApplicationSummaryResponse extends Omit<IApiResponse, 'result'> {
    result: [IApplicationSummary];
}

export interface IApplicationFileSummaryResponse extends Omit<IApiResponse, 'result'> {
    result: [IApplicationFileSummary];
}

export interface ICheckLoanAndBfiNumberResponse extends Omit<IApiResponse, 'result'> {
    result: ICheckLoanAndBfiNumber;
}

export interface IAppraiseApplicationResponse extends Omit<IApiResponse, 'result'> {
    result: IAppraiseApplication;
}

export interface IHighlightsResponse extends Omit<IApiResponse, 'result'> {
    result: IHighlights;
}

export interface IGridApplAppraisalResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IGridAppAppraisal];
    };
}

export interface IGridApplDeclinedResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IGridAppDeclined];
    };
}

export interface IRollbackApplicationResponse extends Omit<IApiResponse, 'result'> {
    result: IRollbackApplication;
}

export interface IGetLoanToConvertResponse extends Omit<IApiResponse, 'result'> {
    result: IGetLoanToConvert;
}

export interface ICanConvertBorrowerResponse extends Omit<IApiResponse, 'result'> {
    result: boolean;
}

export interface IPropertiesDelete {
    propertyIds: [number];
}

export interface IGridAppBaseResponse {
    result: {
        totalCount: number;
        results: [IGridAppBase];
    };
}

export interface IMoveProperties {
    sourceAppId: number;
    targetAppId: number;
    propertyIds: [number];
}

export class ApplicationApi extends BfiApi {
    getRecentApplications(): Promise<IRecentApplicationResponse> {
        return super.get('/applications/recent');
    }

    getSubmittedAppReview(id: number): Promise<ISubmittedAppReviewResponse> {
        return super.get(`/applications/${id}/submitted/review`);
    }

    getIncompleteAppReview(id: number): Promise<IIncompleteAppReviewResponse> {
        return super.get(`/applications/${id}/incomplete/review`);
    }

    getApplicationSummary(id: number): Promise<IApplicationSummaryResponse> {
        return super.get(`/applications/${id}/summary`);
    }

    getApplicationFileSummary(id: number): Promise<IApplicationFileSummaryResponse> {
        return super.get(`/applications/${id}/filesummary`);
    }

    updatePropertyReview(property: IPropertyReviewUpdate): Promise<IApiResponse> {
        return super.post(`/applications/submitted/review`, property);
    }

    updateIncompleteReview(model: IIncompleteAppReviewUpdate): Promise<IApiResponse> {
        return super.post(`/applications/incomplete/review`, model);
    }

    checkLoanAndBfiNumber(model: IBfiNumberCheck): Promise<ICheckLoanAndBfiNumberResponse> {
        return super.post(`/applications/checkbfinumbers`, model);
    }

    getHighlights(): Promise<IHighlightsResponse> {
        return super.get('/applications/highlights');
    }

    getGrid_Incomplete(criteria: ISearchCriteria): Promise<IGridIncompleteResponse> {
        return super.post('/applications/grids/incomplete', criteria);
    }

    getGrid_Rentals(criteria: ISearchCriteria): Promise<IGridApplPropertyResponse> {
        return super.post('/applications/grids/submitted/rentals', criteria);
    }

    getGrid_ByPrincipalId(principalId: number, criteria: ISearchCriteria): Promise<IGridAppBaseResponse> {
        return super.post(`/applications/grids/principals/${principalId}`, criteria);
    }

    getGrid_ByEntityId(entityId: number, criteria: ISearchCriteria): Promise<IGridAppBaseResponse> {
        return super.post(`/applications/grids/entities/${entityId}`, criteria);
    }

    getGrid_NonRentals(criteria: ISearchCriteria): Promise<IGridApplPropertyResponse> {
        return super.post('/applications/grids/submitted/nonrentals', criteria);
    }

    getGrid_LOC(criteria: ISearchCriteria): Promise<IGridApplPropertyResponse> {
        return super.post('/applications/grids/submitted/loc', criteria);
    }

    getGrid_Bridge(criteria: ISearchCriteria): Promise<IGridApplPropertyResponse> {
        return super.post('/applications/grids/submitted/bridge', criteria);
    }

    getGrid_Appraise_BfiToOrder(criteria: ISearchCriteria): Promise<IGridApplPropertyResponse> {
        return super.post('/applications/grids/appraise/bfitoorder', criteria);
    }

    getGrid_Appraise_Ordered(criteria: ISearchCriteria): Promise<IGridApplAppraisalResponse> {
        return super.post('/applications/grids/appraise/ordered', criteria);
    }

    getGrid_Appraise_Received(criteria: ISearchCriteria): Promise<IGridApplAppraisalResponse> {
        return super.post('/applications/grids/appraise/received', criteria);
    }

    getGrid_Appraise_Reviewed(criteria: ISearchCriteria): Promise<IGridApplAppraisalResponse> {
        return super.post('/applications/grids/appraise/reviewed', criteria);
    }

    getGrid_NoFurtherAction(criteria: ISearchCriteria): Promise<IGridApplDeclinedResponse> {
        return super.post(`/applications/grids/declined/nofurtheraction`, criteria);
    }

    getGrid_IncompleteFile(criteria: ISearchCriteria): Promise<IGridApplDeclinedResponse> {
        return super.post(`/applications/grids/declined/incompletefile`, criteria);
    }

    getGrid_RecycleBin(criteria: ISearchCriteria): Promise<IGridApplPropertyResponse> {
        return super.post(`/applications/grids/declined/recyclebin`, criteria);
    }

    getGrid_Approved(criteria: ISearchCriteria): Promise<IGridApplPropertyApprovedResponse> {
        return super.post('/applications/grids/approved', criteria);
    }

    getApplicationAppraisal(id: number, statusTypeId: PropertyStatusType): Promise<IAppraiseApplicationResponse> {
        return super.get(`/applications/${id}/appraise/${statusTypeId}`);
    }

    setApplicationAppraisal(id: number, appraisal: IAppraiseApplication): Promise<IApiResponse> {
        return super.post(`/applications/${id}/appraise`, appraisal);
    }

    getApplicationAppraisalNotes(id: number): Promise<IAppraiseApplicationResponse> {
        return super.get(`/applications/${id}/appraise/notes`);
    }

    setApplicationAppraisalNotes(id: number, appraisal: IAppraiseApplicationNotes): Promise<IApiResponse> {
        return super.post(`/applications/${id}/appraise/notes`, appraisal);
    }

    getDeclinedReject(id: number): Promise<IApiResponse> {
        return super.get(`/applications/${id}/lostdelete`);
    }

    setDeclinedReject(id: number, update: IDeclineRejectApplication): Promise<IApiResponse> {
        return super.post(`/applications/${id}/lostdelete`, update);
    }

    getAppraisalRollback(id: number): Promise<IRollbackApplicationResponse> {
        return super.get(`/applications/${id}/appraise/rollback`);
    }

    setAppraisalRollback(id: number, update: IRollbackApplicationRequest): Promise<IApiResponse> {
        return super.post(`/applications/${id}/appraise/rollback`, update);
    }

    regenerateApplicationFile(fileId: number): Promise<IApiResponse> {
        return super.get(`/applications/files/${fileId}/regenerate`);
    }

    requestApplicationFile(fileId: number): Promise<IApiResponse> {
        return super.get(`/applications/files/${fileId}/status`);
    }

    regeneratePropertyFile(fileId: number): Promise<IApiResponse> {
        return super.get(`/applications/properties/files/${fileId}/regenerate`);
    }

    requestPropertyFileStatus(fileId: number): Promise<IApiResponse> {
        return super.get(`/applications/properties/files/${fileId}/status`);
    }

    getConvertToLoan(id: number): Promise<IGetLoanToConvertResponse> {
        return super.get(`/applications/${id}/appraise/convert`);
    }

    setConvertToLoan(id: number, model: IConvertToLoan): Promise<IApiResponse> {
        return super.post(`/applications/${id}/appraise/convert`, model);
    }

    canConvertBorrowerEntity(id: number): Promise<ICanConvertBorrowerResponse> {
        return super.get(`/applications/${id}/canconvertborrower`);
    }

    getChangeLoanType(applicationId: number): Promise<IApiResponse> {
        return super.get(`/applications/${applicationId}/changeloantype`);
    }

    setChangeLoanType(applicationId: number, model: IChangeLoanType): Promise<IApiResponse> {
        return super.post(`/applications/${applicationId}/changeloantype`, model);
    }

    getRestoreLoan(applicationId: number): Promise<IApiResponse> {
        return super.get(`/applications/${applicationId}/restore`);
    }

    setRestoreLoan(applicationId: number): Promise<IApiResponse> {
        return super.post(`/applications/${applicationId}/restore`);
    }

    getDeleteProperties(id: number): Promise<IApiResponse> {
        return super.get(`/applications/${id}/properties/delete`);
    }

    setDeleteProperties(id: number, model: IPropertiesDelete): Promise<IApiResponse> {
        return super.post(`/applications/${id}/properties/delete`, model);
    }

    getMoveProperties(applicationId: number): Promise<IApiResponse> {
        return super.get(`/applications/${applicationId}/properties/move`);
    }

    getMovePropertiesAppSearch(criteria: ISearchCriteria): Promise<IGridAppBaseResponse> {
        return super.post(`/applications/properties/move/search`, criteria);
    }

    setMovePropertiesAppSearch(applicationId: number, model: IMoveProperties): Promise<IApiResponse> {
        return super.post(`/applications/${applicationId}/properties/move`, model);
    }

    getPrincipalSwap(id: number): Promise<IApiResponse> {
        return super.get(`/applications/${id}/principalswap`);
    }

    setPrincipalSwap(id: number, principalId: number): Promise<IApiResponse> {
        return super.post(`/applications/${id}/principalswap/${principalId}`);
    }

    getAuditHistory(applicationId: number): Promise<IApiResponse> {
        return super.get(`/applications/${applicationId}/auditing`);
    }

    getAuditHistoryDetail(changeId: number): Promise<IApiResponse> {
        return super.get(`applications/auditing/${changeId}`);
    }
}
